import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../config/api";
import "./Login.css";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrId: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const navigate = useNavigate();
  const { login } = useAuth();

  const ADMIN_DASHBOARDS = {
    "super-admin": "/admin/dashboard",
    "state-admin": "/admin/state-dashboard",
    "sd-admin": "/admin/senatorial-dashboard",
    "fc-admin": "/admin/district-dashboard",
    "lga-admin": "/admin/lga-dashboard",
    "lcda-admin": "/admin/lcda-dashboard",
    "ward-admin": "/admin/ward-dashboard",
    "polling-unit-admin": "/admin/polling-unit-dashboard",
    "member": "/profile",
    "local-cordinator": "/profile",
  };

  // Show toast notification
  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 5000);
  };

  // Handle input change for both email and phone
  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, emailOrId: value });
    setError("");
  };

  const handlePasswordChange = (e) => {
    setFormData({ ...formData, password: e.target.value });
    setError("");
  };

  // Function to extract detailed error messages from backend response
  const extractErrorMessage = (error) => {
    console.log("🔍 Full error object for debugging:", error);
    
    // Handle axios error response
    if (error.response?.data) {
      const data = error.response.data;
      console.log("📄 Error response data:", data);
      
      // Customize the "Invalid credentials" message
      if (data.message && data.message.toLowerCase().includes("invalid credential")) {
        return "Invalid phone number/email or password. Please check your details and try again.";
      }
      
      // If there are specific field errors in data.errors
      if (data.errors && typeof data.errors === 'object') {
        const errorMessages = [];
        
        // Loop through each field in errors object
        Object.entries(data.errors).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            // Join all error messages for this field
            const fieldErrors = errors.join(', ');
            // Convert field name to readable format
            const readableField = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            errorMessages.push(`${readableField}: ${fieldErrors}`);
          }
        });
        
        if (errorMessages.length > 0) {
          return errorMessages.join(' • ');
        }
      }
      
      // If there's a direct error message
      if (data.error) {
        return data.error;
      }
      
      // If there's a general message
      if (data.message) {
        return data.message;
      }
    }

    // Handle specific error codes
    if (error.code === "ERR_NETWORK") {
      console.log("🌐 Network error details:", {
        url: error.config?.baseURL + error.config?.url,
        method: error.config?.method,
        timeout: error.config?.timeout
      });
      return "Cannot connect to server. Please check if CORS is configured on the backend server.";
    }
    
    if (error.code === "ECONNABORTED") {
      return "Connection timeout. Please try again.";
    }
    
    if (error.code === "ERR_BAD_REQUEST") {
      return "Invalid request. Please check your input and try again.";
    }

    // Handle network errors
    if (error.message === "Network Error") {
      return "Network error. Please check your internet connection and ensure CORS is enabled on the server.";
    }
    
    // Default error message
    return "Login failed. Please check your phone number/email and password and try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.emailOrId || !formData.password) {
      showToast("Please fill in both fields");
      setLoading(false);
      return;
    }

    // Check if it's a phone number (all digits) and validate length
    const isPhoneNumber = /^\d+$/.test(formData.emailOrId);
    if (isPhoneNumber && formData.emailOrId.length < 10) {
      showToast("Please enter a valid phone number (at least 10 digits)");
      setLoading(false);
      return;
    }

    // Check if it's an email and validate format
    const isEmail = formData.emailOrId.includes('@');
    if (isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emailOrId)) {
        showToast("Please enter a valid email address");
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        identifier: formData.emailOrId,
        password: formData.password,
      };

      console.log("🔄 Attempting login with payload:", payload);
      console.log("🌐 API Base URL:", process.env.REACT_APP_API_BASE_URL);

      // FIXED: Use the correct endpoint without duplicating base path
      const response = await api.post("/auth/login", payload);

      console.log("✅ Login response received:", response);
      console.log("📊 Response data:", response.data);

      if (response.data.status === "success") {
        const userData = response.data.data.user;
        const token = response.data.data.token;

        console.log("👤 User data received:", userData);
        console.log("🔑 Token received:", token ? "Yes" : "No");

        // Store tokens
        localStorage.setItem('jwt_token', token);
        localStorage.setItem('current_user_id', userData.id.toString());
        localStorage.setItem('user_data', JSON.stringify(userData));
        
        // Set last activity timestamp for auto-logout
        localStorage.setItem('lastActivity', Date.now().toString());

        let redirectPath = "/profile";
        const primaryRole = userData.roles?.[0] || "member";
        redirectPath = ADMIN_DASHBOARDS[primaryRole] || "/profile";

        const isAdminUser = primaryRole.includes("admin");
        const adminRoleType = primaryRole.replace("-", " ").toUpperCase();

        const userWithRole = {
          ...userData,
          role: primaryRole,
          isAdminUser,
          adminRoleType,
        };

        console.log("🚀 Logging in user with role:", primaryRole);
        console.log("📍 Redirecting to:", redirectPath);

        login(userWithRole, token);
        localStorage.setItem("tokenExpires", Date.now() + 3600 * 1000);

        setShowSuccess(true);
        setTimeout(() => {
          navigate(redirectPath);
        }, 2000);
      } else {
        console.warn("⚠️ Login response status not 'success':", response.data);
        const errorMsg = extractErrorMessage({ response });
        showToast(errorMsg);
      }
    } catch (error) {
      console.error("❌ Login error caught:", error);
      console.error("🔧 Error details:", {
        name: error.name,
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          timeout: error.config?.timeout
        },
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : 'No response'
      });
      
      const errorMsg = extractErrorMessage(error);
      showToast(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccess(false);
    navigate("/profile");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="login-page">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">✓</div>
            <h3>Welcome Back!</h3>
            <p>You have successfully logged in to your account.</p>

            <div className="success-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <span>Redirecting to your dashboard...</span>
            </div>
            <button className="success-close-btn" onClick={closeSuccessModal}>
              Go to Dashboard
            </button>
          </div>
        </div>
      )}

      <div className="login-container">
        <h2>Welcome Back Member</h2>

        <form onSubmit={handleSubmit}>
          <label>Phone Number or Email</label>
          <input
            type="text"
            name="emailOrId"
            placeholder="Enter your phone number or email"
            value={formData.emailOrId}
            onChange={handleIdentifierChange}
            disabled={loading}
          />

          <label>Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handlePasswordChange}
              disabled={loading}
            />
            <div className="password-toggle" onClick={togglePasswordVisibility}>
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </div>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? (
              <>
                <div className="spinner"></div>
                Signing In...
              </>
            ) : (
              "Sign In to Dashboard"
            )}
          </button>
        </form>

        <p className="forgot">
          Forgot password? <Link to="/reset">Reset here</Link>
        </p>

        <p className="register">
          Not a member yet?{" "}
          <Link to="/register">Register to join the movement.</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;